/**
 * Os compromissos da casa — a coleção `calendar_events`.
 *
 * O modelo é o `CalendarEvent` do Flutter, campo por campo: o nome gravado é o
 * contrato entre os dois apps, então `type` continua guardando `dateNight` e
 * não "Encontro". O que muda de nome aqui é só o que aparece na tela.
 */

import { lerData, gravarData } from '../nucleo/tempo';
import type { NomeDoIcone } from '../theme/desenhos';

/** Os quatro do `CalendarEventType`, na ordem em que o enum os declara. */
export type TipoDeCompromisso = 'dateNight' | 'anniversary' | 'reminder' | 'custom';

/** O `recurring` do Flutter: três palavras, gravadas cruas. */
export type Repeticao = 'none' | 'monthly' | 'yearly';

export interface Compromisso {
  id: string;
  titulo: string;
  descricao: string;
  quando: Date;
  fim: Date | null;
  tipo: TipoDeCompromisso;
  criadoPor: string;
  /** Uma cor escolhida à mão. Ninguém escolhe ainda; o campo já existe lá. */
  cor: string | null;
  repete: Repeticao;
}

/**
 * O desenho e a palavra de cada tipo (`calendarEventTypeInfo`) junto com a cor
 * de cada um (`calendarEventHue`). Lá são duas tabelas em dois arquivos; aqui
 * é uma só, porque quem mostra o desenho é sempre quem pinta a borda.
 *
 * O `icone` não tem par no Flutter: lá o tipo é um emoji em toda parte
 * (`💑`, `💍`, `⏰`, `📌`). Aqui a casa tem regra de não pôr emoji em tela, e
 * cada tipo aponta o traço da biblioteca que o representa — o coração do
 * encontro, o brilho do aniversário, o relógio do lembrete, o alfinete do
 * resto. Os seis lugares que mostram um tipo mostram todos o mesmo traço: o
 * emoji não sobrou em nenhum, porque um selo com emoji ao lado de um selo com
 * desenho, na mesma tela, faz o mesmo compromisso parecer duas coisas.
 */
export const TIPOS_DE_COMPROMISSO: Array<{
  chave: TipoDeCompromisso;
  icone: NomeDoIcone;
  nome: string;
  cor: string;
}> = [
  { chave: 'dateNight', icone: 'coracao', nome: 'Encontro', cor: 'var(--aurora-rosa)' },
  { chave: 'anniversary', icone: 'brilho', nome: 'Aniversário', cor: 'var(--ouro)' },
  { chave: 'reminder', icone: 'relogio', nome: 'Lembrete', cor: 'var(--lavanda-viva)' },
  { chave: 'custom', icone: 'alfinete', nome: 'Outro', cor: 'var(--lavanda)' },
];

const POR_CHAVE = new Map(TIPOS_DE_COMPROMISSO.map((t) => [t.chave, t]));

const doTipo = (t: TipoDeCompromisso) => POR_CHAVE.get(t) ?? TIPOS_DE_COMPROMISSO[3];

export const iconeDoCompromisso = (t: TipoDeCompromisso): NomeDoIcone => doTipo(t).icone;
export const nomeDoCompromisso = (t: TipoDeCompromisso): string => doTipo(t).nome;
export const corDoCompromisso = (t: TipoDeCompromisso): string => doTipo(t).cor;

/** As palavras do seletor de repetição, na ordem do `DropdownButton` de lá. */
export const REPETICOES: Array<{ chave: Repeticao; nome: string }> = [
  { chave: 'none', nome: 'Nenhuma' },
  { chave: 'monthly', nome: 'Todo mês' },
  { chave: 'yearly', nome: 'Todo ano' },
];

export const nomeDaRepeticao = (r: Repeticao): string =>
  REPETICOES.find((x) => x.chave === r)?.nome ?? 'Nenhuma';

function tipoDe(valor: unknown): TipoDeCompromisso {
  return POR_CHAVE.has(valor as TipoDeCompromisso) ? (valor as TipoDeCompromisso) : 'custom';
}

function repeticaoDe(valor: unknown): Repeticao {
  return valor === 'monthly' || valor === 'yearly' ? valor : 'none';
}

export function compromissoDeMapa(id: string, dados: Record<string, unknown>): Compromisso {
  return {
    id,
    titulo: typeof dados.title === 'string' ? dados.title : '',
    descricao: typeof dados.description === 'string' ? dados.description : '',
    // Sem data o compromisso vira "agora", como o `?? DateTime.now()` de lá:
    // some da lista do dia certo, mas não derruba a tela inteira.
    quando: lerData(dados.date) ?? new Date(),
    fim: lerData(dados.endDate),
    tipo: tipoDe(dados.type),
    criadoPor: typeof dados.createdBy === 'string' ? dados.createdBy : '',
    cor: typeof dados.color === 'string' ? dados.color : null,
    repete: repeticaoDe(dados.recurring),
  };
}

export function compromissoParaMapa(e: Compromisso): Record<string, unknown> {
  return {
    title: e.titulo,
    description: e.descricao,
    date: gravarData(e.quando),
    // `endDate` só entra quando existe, igual ao `if (endDate != null)` de lá:
    // gravar `null` deixaria o campo vazio no documento em vez de fora dele.
    ...(e.fim ? { endDate: gravarData(e.fim) } : {}),
    type: e.tipo,
    createdBy: e.criadoPor,
    color: e.cor,
    recurring: e.repete,
  };
}

export const mesmoDia = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const dentro = (quando: Date, inicio: Date, fim: Date): boolean =>
  quando >= inicio && quando <= fim;

/** O último dia de um mês: o dia zero do mês seguinte, que é o de sempre. */
const ultimoDia = (ano: number, mes: number): number => new Date(ano, mes + 1, 0).getDate();

/**
 * Uma cópia do compromisso em outra data, com a mesma hora.
 *
 * O dia é preso ao fim do mês: um lembrete do dia 31 cai no dia 28 em
 * fevereiro, e não vaza para março como faria um `new Date(ano, 1, 31)`.
 */
function em(e: Compromisso, ano: number, mes: number): Compromisso {
  const dia = Math.min(e.quando.getDate(), ultimoDia(ano, mes));
  const quando = new Date(ano, mes, dia);
  quando.setHours(
    e.quando.getHours(),
    e.quando.getMinutes(),
    e.quando.getSeconds(),
    e.quando.getMilliseconds(),
  );
  return { ...e, quando };
}

/**
 * As vezes em que um compromisso cai dentro de uma janela.
 *
 * Um compromisso que não repete cai uma vez, ou nenhuma. Um que repete todo
 * mês ou todo ano volta a cair — e é isto que faltava: o campo `recurring`
 * era gravado pelo formulário e depois ninguém o lia, então quem marcava o
 * aniversário como "todo ano" nunca mais o via. A repetição nunca anda para
 * trás da data original: a data de origem é o começo, não um exemplo.
 *
 * A cópia mantém o `id` do documento, porque é ele que apaga. Quem lista duas
 * ocorrências do mesmo compromisso precisa de uma chave com a data junto.
 */
export function ocorrenciasEntre(e: Compromisso, inicio: Date, fim: Date): Compromisso[] {
  if (e.repete === 'none') return dentro(e.quando, inicio, fim) ? [e] : [];
  if (e.quando > fim) return [];

  const saida: Compromisso[] = [];
  const passo = e.repete === 'yearly' ? 12 : 1;

  // Anda de mês em mês contando a partir do mês da data original, e começa no
  // primeiro passo que já alcança a janela em vez de varrer os anos de trás.
  const mesesDe = (d: Date) => d.getFullYear() * 12 + d.getMonth();
  const zero = mesesDe(e.quando);
  const primeiro = Math.max(0, Math.ceil((mesesDe(inicio) - zero) / passo));
  const ultimo = Math.floor((mesesDe(fim) - zero) / passo);

  for (let n = primeiro; n <= ultimo; n++) {
    const total = zero + n * passo;
    const cada = em(e, Math.floor(total / 12), total % 12);
    if (cada.quando >= e.quando && dentro(cada.quando, inicio, fim)) saida.push(cada);
  }
  return saida;
}

const porData = (a: Compromisso, b: Compromisso) => a.quando.getTime() - b.quando.getTime();

function naJanela(compromissos: Compromisso[], inicio: Date, fim: Date): Compromisso[] {
  return compromissos.flatMap((e) => ocorrenciasEntre(e, inicio, fim)).sort(porData);
}

/** Os compromissos de um mês, o `getEventsForMonth`. */
export function compromissosDoMes(compromissos: Compromisso[], mes: Date): Compromisso[] {
  const inicio = new Date(mes.getFullYear(), mes.getMonth(), 1);
  const fim = new Date(mes.getFullYear(), mes.getMonth() + 1, 0, 23, 59, 59, 999);
  return naJanela(compromissos, inicio, fim);
}

/** Os de um dia só, o `getEventsForDay`. */
export function compromissosDoDia(compromissos: Compromisso[], dia: Date): Compromisso[] {
  const inicio = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate());
  const fim = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), 23, 59, 59, 999);
  return naJanela(compromissos, inicio, fim);
}

/** O que vem pela frente, o `getUpcomingEvents`. */
export function proximosCompromissos(
  compromissos: Compromisso[],
  dias: number,
  agora = new Date(),
): Compromisso[] {
  const fim = new Date(agora.getTime() + dias * 86_400_000);
  return naJanela(compromissos, agora, fim);
}

/**
 * "Hoje", "Amanhã" ou "Em N dias" — o `timeLabel` do cartão de "em breve".
 *
 * Conta dias de calendário e não de 24 horas: um compromisso das 9h de amanhã
 * visto às 22h de hoje é "Amanhã", e o `inDays` do Dart o chamaria de "Hoje".
 */
export function quandoEmPalavras(quando: Date, agora = new Date()): string {
  const meiaNoite = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dias = Math.round((meiaNoite(quando) - meiaNoite(agora)) / 86_400_000);
  if (dias <= 0) return 'Hoje';
  if (dias === 1) return 'Amanhã';
  return `Em ${dias} dias`;
}
